import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { liveContextQueryOptions } from '@renderer/hooks/useLiveContext'
import { useWritingStyleIntegrations, type WritingStyleIntegration } from './use-writing-style-integrations'

export interface WritingStyleExcerpt {
  integration: WritingStyleIntegration
  text: string
}

/**
 * The first line of prose under the document's first heading (or from the top
 * when it has none), with markdown syntax removed.
 */
function firstProseLine(markdown: string): string | null {
  const lines = markdown.split('\n')
  const firstHeadingIndex = lines.findIndex((line) => line.trim().startsWith('#'))
  for (const rawLine of lines.slice(firstHeadingIndex + 1)) {
    const line = rawLine.trim()
    if (!line || /^(#|>|\||[-*_]{3,}$)/.test(line)) {
      continue
    }
    const text = line
      .replace(/^([-*+]|\d+\.)\s+/, '')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/\*\*|__|\*|`/g, '')
      .trim()
    if (text) {
      return text
    }
  }
  return null
}

/** One excerpt per learned writing style, for previewing them at a glance. */
export function useWritingStyleExcerpts(): { excerpts: Array<WritingStyleExcerpt>; isLoading: boolean } {
  const { getAuthToken } = useAuth()
  const { integrations, isLoading: isLoadingIntegrations } = useWritingStyleIntegrations()

  const { contexts, isLoadingContexts } = useQueries({
    queries: integrations.map((integration) => liveContextQueryOptions(integration.environmentName, getAuthToken)),
    combine: (results) => ({
      contexts: results.map((result) => result.data?.context),
      isLoadingContexts: results.some((result) => result.isLoading),
    }),
  })

  const excerpts = useMemo(
    () =>
      integrations.flatMap((integration, index) => {
        const text = firstProseLine(contexts[index] ?? '')
        return text ? [{ integration, text }] : []
      }),
    [integrations, contexts],
  )

  return { excerpts, isLoading: isLoadingIntegrations || isLoadingContexts }
}
